import { EventFormat } from "../types/events-unified";

/**
 * AD-395. Есть ли у события ОНЛАЙН-ЧАСТЬ — один ответ на весь фронт.
 *
 * ПОЧЕМУ КАРТА, А НЕ `format !== OFFLINE`. У `Record<EventFormat, boolean>` нет
 * умолчания: добавят в словарь четвёртый формат — сборка встанет, пока его
 * сознательно не отнесут к одной из сторон. Условие «не очно» молча зачислило
 * бы новый формат в онлайн, то есть пообещало бы человеку встречу там, где о
 * ней никто не думал.
 *
 * ЗЕРКАЛО СЕРВЕРА. Тот же приём и по той же причине стоит на бэкенде —
 * `ONLINE_ACCESS_BY_FORMAT` в `modules/events/constants/online-access.constants.ts`.
 * Общего рантайм-словаря у репозиториев нет, поэтому карты обязаны отвечать
 * одинаково: если фронт считает, что онлайн-часть есть, а сервер — что нет,
 * человек получит кнопку, ведущую в отказ. Меняете одну — меняйте обе.
 */
export const ONLINE_PART_BY_FORMAT: Record<EventFormat, boolean> = {
  [EventFormat.OFFLINE]: false,
  [EventFormat.ONLINE]: true,
  [EventFormat.HYBRID]: true,
};

/**
 * Есть ли у события онлайн-часть. Формат в типе события необязательный, и от
 * старого сервера ответ может прийти вовсе без него; в этом случае считаем,
 * что онлайн-части НЕТ. Из двух ошибок выбрана та, что не обещает лишнего:
 * потерянная кнопка заметна и чинится, а кнопка, ведущая в отказ, выглядит
 * поломкой продукта.
 */
export const hasOnlinePart = (format: EventFormat | undefined): boolean =>
  format !== undefined && ONLINE_PART_BY_FORMAT[format] === true;

/**
 * AD-389 шаг 6. СПРАШИВАЕТ ЛИ ЭТОТ ФОРМАТ О СПОСОБЕ УЧАСТИЯ.
 *
 * Зеркало BE `PARTICIPATION_MODE_CHOICE_BY_FORMAT`
 * (`modules/events/constants/participation-mode.constants.ts`). У очного и у
 * онлайнового события выбирать не из чего — способ уже выражен форматом, и
 * присланное значение сервер отвергнет кодом
 * `PARTICIPATION_MODE_NOT_APPLICABLE`. Спросить там значило бы показать выбор,
 * который ведёт в отказ.
 *
 * ПОЧЕМУ ОТДЕЛЬНАЯ КАРТА, А НЕ `!ONLINE_PART_BY_FORMAT`. Соседняя карта
 * отвечает на ДРУГОЙ вопрос — есть ли у события онлайн-часть, — и у чисто
 * онлайнового события ответ там `true`, а здесь должен быть `false`. Сегодня
 * обе сводятся к формату, но выражать одну через другую значит склеить два
 * правила, которые разойдутся при первом же новом формате: «гибрид с записью»
 * имел бы онлайн-часть, не спрашивая о способе.
 *
 * Умолчания у `Record<EventFormat, boolean>` нет: новый формат не
 * скомпилируется, пока его сознательно не отнесут к одной из сторон.
 */
export const PARTICIPATION_MODE_CHOICE_BY_FORMAT: Record<EventFormat, boolean> =
  {
    [EventFormat.OFFLINE]: false,
    [EventFormat.ONLINE]: false,
    [EventFormat.HYBRID]: true,
  };

/**
 * Спрашивает ли событие о способе участия. Формат в типе события
 * необязателен, и ответ старого сервера может прийти вовсе без него; тогда —
 * НЕ спрашиваем: умолчание здесь запрет, как и на бэкенде. Непоказанный выбор
 * оставляет «не заявлен» — состояние честное; показанный на негибриде привёл
 * бы человека к 400 на записи.
 */
export const asksParticipationMode = (
  format: EventFormat | undefined,
): boolean =>
  format !== undefined && PARTICIPATION_MODE_CHOICE_BY_FORMAT[format] === true;

/**
 * AD-411. ТОТ ЖЕ ВОПРОС О ФОРМАТЕ, ПРИЕХАВШЕМ СТРОКОЙ.
 *
 * Не всякий экран получает событие целиком: кабинет участника живёт списком
 * `/participant/events/upcoming`, где формат объявлен `string`. Приводить эту
 * строку к `EventFormat` значило бы утверждать о значении, которого никто не
 * проверял, — поэтому карта не копируется, а ПРИСВАИВАЕТСЯ в расширенный тип:
 * исчерпывающая проверка остаётся на исходной (новый формат не скомпилируется,
 * пока его не отнесут к стороне), а эта только читает. Незнакомая строка
 * отвечает «не спрашиваем» — то же умолчание-запрет, что и выше.
 *
 * Тот же приём, что у `OWN_STATUS_BY_STATUS` в `AD-8`.
 */
const PARTICIPATION_MODE_CHOICE_BY_NAME: Record<string, boolean> =
  PARTICIPATION_MODE_CHOICE_BY_FORMAT;

export const asksParticipationModeByName = (
  format: string | undefined,
): boolean =>
  format !== undefined && PARTICIPATION_MODE_CHOICE_BY_NAME[format] === true;
